import { initMatomoTagManager, pushMtmData } from './matomoTagManager'

// ---------------------------------------------------------------------------
// Types — exported so components can import and build strongly-typed payloads
// ---------------------------------------------------------------------------

export type PageType =
  | 'page'
  | 'search_result'
  | 'category'
  | 'product_detail'
  | 'cart'
  | 'checkout'
  | 'confirmation'

export interface IPageViewData {
  page_type: PageType
  page_language: string
  selected_country: string
  section?: string
  canonical_url: string
  consultant_name?: string
  consultant_id?: number
  flow_type?: string
}

export interface IMatomoItem {
  item_id: string
  item_name: string
  affiliation: string
  item_brand: string
  item_category: string
  item_category2?: string
  item_category3?: string
  item_variant?: string
  price: number
  quantity: number
  coupon?: string
  discount?: number
}

export interface IPurchaseData {
  transaction_id: string
  value: number
  tax: number
  shipping: number
  currency: string
  coupon?: string
  customer_type: 'new' | 'returning'
  funnel_type: 'individual' | 'party' | 'recruitment'
  items: IMatomoItem[]
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let initialized = false
let lastCheckoutValue = 0
let lastCheckoutCurrency = 'EUR'
let lastCheckoutFunnelType: 'individual' | 'party' | 'recruitment' = 'individual'

/** Push { ecommerce: null } before every ecommerce event to avoid data bleed */
function resetEcommerce(): void {
  pushMtmData({ ecommerce: null })
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Initialize Matomo Tag Manager.
 * Call once on app startup, passing the full container CDN URL
 * (provided by Umbraco via AOEConfig.MatomoContainerUrl).
 *
 * When `loadedInHead` is true the host (web2) has already loaded the container
 * synchronously in the <head> — ahead of the consent manager — so we must NOT
 * inject it again here, or the container would load twice and double-count
 * events. In that case we only flip `initialized` so the track* helpers push
 * into the existing `_mtm` data layer.
 *
 * No-op if containerUrl is empty — allows pages without Matomo to work safely.
 */
export function initAnalytics(containerUrl: string, loadedInHead = false): void {
  if (loadedInHead) {
    window._mtm = window._mtm || []
    initialized = true
    console.info('[Matomo] Container loaded in <head>; skipping bundle injection')
    return
  }
  if (!containerUrl) return
  initMatomoTagManager(containerUrl)
  initialized = true
  console.info('[Matomo] Tag Manager initialized:', containerUrl)
}

// ---------------------------------------------------------------------------
// Page & Navigation Events
// ---------------------------------------------------------------------------

/**
 * Push base data layer on every page load.
 * Should be called as early as possible on each page.
 * Umbraco-rendered pages may call this from Razor; Vue components call it in onMounted.
 */
export function pushPageViewDataLayer(data: IPageViewData): void {
  if (!initialized) return
  pushMtmData({
    page_type: data.page_type,
    page_language: data.page_language,
    selected_country: data.selected_country,
    section: data.section ?? null,
    canonical_url: data.canonical_url,
    consultant_name: data.consultant_name ?? null,
    consultant_id: data.consultant_id ?? null,
    flow_type: data.flow_type ?? null,
  })
}

/**
 * mtm.search — triggered when a user performs a search.
 */
export function trackSearch(searchPhrase: string, searchResultsCount: number): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'mtm.search',
    search_phrase: searchPhrase,
    search_results_count: searchResultsCount,
  })
}

/**
 * view_item_list — triggered when a visitor views a category or product list page.
 */
export function trackViewItemList(itemListName: string): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'view_item_list',
    ecommerce: {
      item_list_name: itemListName,
    },
  })
}

// ---------------------------------------------------------------------------
// User Events
// ---------------------------------------------------------------------------

/**
 * register — triggered when a new user completes registration.
 * No personal data is collected.
 */
export function trackRegister(userType: 'consultant' | 'customer' = 'customer'): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'register',
    user_type: userType,
  })
}

/**
 * login — triggered when a user successfully logs in.
 * No personal data is collected.
 */
export function trackLogin(userType: 'consultant' | 'customer' = 'customer'): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'login',
    user_type: userType,
  })
}

/**
 * consultant_selected — triggered when a user selects or changes their consultant.
 */
export function trackConsultantSelected(consultantName: string, consultantId: number): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'consultant_selected',
    consultant_name: consultantName,
    consultant_id: consultantId,
  })
}

// ---------------------------------------------------------------------------
// Product Events
// ---------------------------------------------------------------------------

/**
 * view_item — triggered when a visitor views a product detail page.
 */
export function trackViewItem(item: IMatomoItem, currency: string = 'EUR'): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'view_item',
    ecommerce: {
      currency,
      value: item.price * item.quantity,
      items: [item],
    },
  })
}

/**
 * add_to_cart — triggered when a product is added to the cart.
 * IMPORTANT: items must be ALL products currently in the cart, not just the newly added item.
 */
export function trackAddToCart(
  allCartItems: IMatomoItem[],
  totalCartValue: number,
  currency: string = 'EUR',
): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'add_to_cart',
    ecommerce: {
      currency,
      value: totalCartValue,
      items: allCartItems,
    },
  })
}

// ---------------------------------------------------------------------------
// Cart Events
// ---------------------------------------------------------------------------

/**
 * view_cart — triggered when a visitor opens or views the shopping cart.
 */
export function trackViewCart(
  items: IMatomoItem[],
  totalValue: number,
  currency: string = 'EUR',
): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'view_cart',
    ecommerce: {
      currency,
      value: totalValue,
      items,
    },
  })
}

/**
 * coupon_applied — triggered when a user successfully applies a coupon code.
 */
export function trackCouponApplied(
  coupon: string,
  discountValue: number,
  currency: string = 'EUR',
): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'coupon_applied',
    ecommerce: {
      currency,
      coupon,
      discount_value: discountValue,
    },
  })
}

/**
 * Helper to determine funnel_type based on orderTypeId and eventId.
 */
export function getFunnelType(
  orderTypeId?: string | null,
  eventId?: number | null,
): 'individual' | 'party' | 'recruitment' {
  const oId = orderTypeId?.toUpperCase() ?? ''

  if (oId.includes('G')) {
    return 'recruitment'
  }

  if (eventId) {
    return 'party'
  }

  return 'individual'
}

/**
 * Select_starter_kit — triggered when selectKit button from kit-actions is clicked.
 * Same behavior as add_to_cart event.
 */
export function trackSelectStarterKit(
  allCartItems: IMatomoItem[],
  totalCartValue: number,
  currency: string = 'EUR',
): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'Select_starter_kit',
    ecommerce: {
      currency,
      value: totalCartValue,
      items: allCartItems,
    },
  })
}

// ---------------------------------------------------------------------------
// Checkout Events
// ---------------------------------------------------------------------------

/**
 * begin_checkout — triggered when a visitor begins the checkout process.
 */
export function trackBeginCheckout(
  items: IMatomoItem[],
  value: number,
  currency: string = 'EUR',
  funnel_type: 'individual' | 'party' | 'recruitment' = 'individual',
  coupon?: string,
): void {
  if (!initialized) return
  lastCheckoutValue = value
  lastCheckoutCurrency = currency
  lastCheckoutFunnelType = funnel_type
  resetEcommerce()
  pushMtmData({
    event: 'begin_checkout',
    ecommerce: {
      currency,
      value,
      funnel_type,
      ...(coupon ? { coupon } : {}),
      items,
    },
  })
}

/**
 * add_personal_info — Step 1: user completes personal information (name, phone).
 * No personal data is collected in the event itself.
 */
export function trackAddPersonalInfo(
  value: number,
  currency: string = 'EUR',
  funnel_type: 'individual' | 'party' | 'recruitment' = 'individual',
): void {
  if (!initialized) return
  const finalValue = value || lastCheckoutValue
  const finalCurrency = currency || lastCheckoutCurrency
  const finalFunnelType = lastCheckoutFunnelType || funnel_type
  pushMtmData({
    event: 'add_personal_info',
    ecommerce: {
      currency: finalCurrency,
      value: finalValue,
      funnel_type: finalFunnelType,
    },
  })
}

/**
 * add_shipping_info — Step 2: user completes shipping address.
 */
export function trackAddShippingInfo(
  value: number,
  currency: string = 'EUR',
  funnel_type: 'individual' | 'party' | 'recruitment' = 'individual',
): void {
  if (!initialized) return
  const finalValue = value || lastCheckoutValue
  const finalCurrency = currency || lastCheckoutCurrency
  const finalFunnelType = lastCheckoutFunnelType || funnel_type
  pushMtmData({
    event: 'add_shipping_info',
    ecommerce: {
      currency: finalCurrency,
      value: finalValue,
      funnel_type: finalFunnelType,
    },
  })
}

/**
 * add_payment_info — Step 3: user selects a payment method.
 */
export function trackAddPaymentInfo(
  paymentType: string,
  value: number,
  currency: string = 'EUR',
  funnel_type: 'individual' | 'party' | 'recruitment' = 'individual',
): void {
  if (!initialized) return
  const finalValue = value || lastCheckoutValue
  const finalCurrency = currency || lastCheckoutCurrency
  pushMtmData({
    event: 'add_payment_info',
    ecommerce: {
      payment_type: paymentType,
      currency: finalCurrency,
      value: finalValue,
      funnel_type,
    },
  })
}

/**
 * purchase — triggered when the order is successfully completed (thank you page).
 * funnel_type: 'individual' for direct purchases, 'party' for party orders,
 * 'recruitment' for recruitment orders.
 */
export function trackPurchase(data: IPurchaseData): void {
  if (!initialized) return
  resetEcommerce()
  pushMtmData({
    event: 'purchase',
    ecommerce: {
      transaction_id: data.transaction_id,
      value: data.value,
      tax: data.tax,
      shipping: data.shipping,
      currency: data.currency,
      ...(data.coupon ? { coupon: data.coupon } : {}),
      customer_type: data.customer_type,
      funnel_type: data.funnel_type,
      items: data.items,
    },
  })
}
